// src/components/admin/AdminPage.tsx
import React, { useEffect, useState } from 'react';
import { useBrandsStore, useIngredientsStore, useProblemsStore, useSkinTypesStore } from '../store/adminStore';
import AddProduct from '../components/product/AddProduct';
import { UserStore, useUserStore } from '../store/userStore';
import { useNavigate } from 'react-router-dom';
import AdminStats from '../components/admin/AdminStats';
import UpdateProduct from '../components/admin/UpdateProduct';
import BrandManagement from '../components/admin/BrandManagement';
import AdminUsersManagement from '../components/admin/AdminUsersManagement';
import AdminNotificationPhoneSettings from '../components/admin/AdminNotificationPhoneSettings';
import ProductListForEdit from '../components/admin/ProductListForEdit';
import { Product } from '../types';
import Notification from '../components/toast/Notification';

const AdminPage = () => {
  const user = useUserStore((state: UserStore) => state.user);
  const isAdmin = user?.isAdmin;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('addProduct');
  const [productToUpdate, setProductToUpdate] = useState<Product | null>(null);
  const [notification, setNotification] = useState<{ id: number, text: string; status: "success" | "error" | "info" } | null>(null);

  if (!isAdmin) {
    navigate('/profile');
  }

  const { fetchBrands } = useBrandsStore();
  const { fetchIngredients } = useIngredientsStore();
  const { fetchProblems } = useProblemsStore();
  const { fetchSkinTypes } = useSkinTypesStore();

  useEffect(() => {
    fetchBrands();
    fetchIngredients();
    fetchProblems();
    fetchSkinTypes();
  }, [fetchBrands, fetchIngredients, fetchProblems, fetchSkinTypes]);

  const handleFixProduct = (product: Product) => {
    setProductToUpdate(product);
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'addProduct':
        return <AddProduct />;
      case 'adminStats':
        return <AdminStats setActiveTab={setActiveTab} onFix={handleFixProduct} />;
      case 'updateProduct':
        if (!productToUpdate) {
          return <ProductListForEdit onSelectProduct={(p) => setProductToUpdate(p)} />;
        }
        return (
          <UpdateProduct
            productToUpdate={productToUpdate}
            setActiveTab={setActiveTab}
            onBackToList={() => setProductToUpdate(null)}
          />
        );
      case 'brandManagement':
        return <BrandManagement />;
      case 'adminUsers':
        return <AdminUsersManagement />;
      case 'settings':
        return <AdminNotificationPhoneSettings />;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-4">
      {notification && <Notification id={notification.id || Date.now()} text={notification.text} status={notification.status} duration={15000} />}
      <div className="tabs flex flex-1 border-b mb-4">
        <button
          className={`tab ${activeTab === 'addProduct' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
          onClick={() => setActiveTab('addProduct')}
        >
          Добавление товара
        </button>
        <button
          className={`tab ${activeTab === 'adminStats' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
          onClick={() => setActiveTab('adminStats')}
        >
          Статистика
        </button>
        <button
          className={`tab ${activeTab === 'updateProduct' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
          onClick={() => { setProductToUpdate(null); setActiveTab('updateProduct'); }}
        >
          Редактировать товар
        </button>
        <button
          className={`tab ${activeTab === 'brandManagement' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
          onClick={() => setActiveTab('brandManagement')}
        >
          Управление брендами
        </button>
        {isAdmin && (
          <button
            className={`tab ${activeTab === 'adminUsers' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
            onClick={() => setActiveTab('adminUsers')}
          >
            Управление админами
          </button>
        )}
        {isAdmin && (
          <button
            className={`tab ${activeTab === 'settings' ? 'border-b-2 border-blue-500' : ''} px-4 py-2`}
            onClick={() => setActiveTab('settings')}
          >
            Настройки
          </button>
        )}
        <a
          href="/admin/sync"
          className="tab px-4 py-2 hover:bg-gray-100 transition-colors flex items-center gap-2"
        >
          <span>🔄</span>
          Синхронизация со складом
        </a>
      </div>
      <div className="tab-content">{renderActiveTab()}</div>
    </div>
  );
};

export default AdminPage;
